import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompanyService } from '../../services/CompanyService/company';
import { AuthService } from '../../services/AuthService/auth';
import { UserResponse } from '../../models/company.dto';
import { Role } from '../../models/auth.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { FilterSelect, FilterOption } from '../../shared/components/filter-select/filter-select';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';
import { CreateCompanyUserDialog } from '../../dialogs/create-company-user-dialog/create-company-user-dialog';
import { ConfirmDialog } from '../../dialogs/confirm-dialog/confirm-dialog';
import { DisableUserDialog } from '../../dialogs/disable-user-dialog/disable-user-dialog';

// A company's own user list is already scoped to it, so the only roles that ever appear here are
// the two company-scoped ones — see shared/role-families.ts for the platform/company role split.
const ROLES: Role[] = ['COMPANY_ADMIN', 'COMPANY_USER'];

function titleCase(role: string): string {
  return role.charAt(0) + role.slice(1).toLowerCase().replace(/_/g, ' ');
}

@Component({
  selector: 'app-company-users',
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, StatusBadge, ErrorBanner, EmptyState, FilterSelect, PageSizeSelect],
  templateUrl: './company-users.html',
  styleUrl: './company-users.scss',
})
export class CompanyUsers implements OnInit {
  roleOptions: FilterOption[] = ROLES.map((r) => ({ value: r, label: titleCase(r) }));
  roleFilter: Role | '' = '';

  users = signal<UserResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  togglingUserId = signal<string | null>(null);

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);

  constructor(private company: CompanyService, private auth: AuthService, private dialog: MatDialog) {}

  get ownUserId(): string | undefined {
    return this.auth.getSession()?.userId;
  }

  // The backend's GET /companies/me/users has no role/status query param — it always returns the
  // whole company roster — so filtering happens client-side over the full list.
  get filteredUsers(): UserResponse[] {
    const list = this.users();
    return this.roleFilter ? list.filter((u) => u.role === this.roleFilter) : list;
  }

  get roleFilterLabel(): string {
    return this.roleFilter ? titleCase(this.roleFilter) : 'All roles';
  }

  get totalCount(): number {
    return this.filteredUsers.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize()));
  }

  get pagedUsers(): UserResponse[] {
    const start = this.page() * this.pageSize();
    return this.filteredUsers.slice(start, start + this.pageSize());
  }

  get rangeStart(): number {
    return this.totalCount === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount);
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
  }

  onFilterChange(): void {
    this.page.set(0);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.page()) {
      return;
    }
    this.page.set(page);
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.page.set(0);

    this.company.listUsers().subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.users.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load users.'));
      },
    });
  }

  openCreateDialog(): void {
    this.dialog
      .open(CreateCompanyUserDialog, { width: '480px' })
      .afterClosed()
      .subscribe((created) => {
        if (created) {
          this.load();
        }
      });
  }

  toggleUser(user: UserResponse): void {
    // Disabling asks for a reason, because the user is shown it the next time they try to sign in
    // and otherwise has no way to find out why they were locked out. Enabling needs no reason, so
    // it stays a plain confirm.
    if (user.enabled) {
      this.dialog
        .open(DisableUserDialog, {
          width: '440px',
          data: { userId: user.id, userEmail: user.email, scope: 'company' },
        })
        .afterClosed()
        .subscribe((disabled) => {
          if (disabled) {
            this.load();
          }
        });
      return;
    }

    this.dialog
      .open(ConfirmDialog, {
        width: '420px',
        data: {
          title: 'Enable this user?',
          message: `"${user.email}" will regain access and be able to log in again.`,
          confirmLabel: 'Enable',
          cancelLabel: 'Cancel',
          danger: false,
          icon: 'check_circle',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.applyToggle(user);
        }
      });
  }

  /** Enable only — disabling goes through DisableUserDialog, which collects the reason itself. */
  private applyToggle(user: UserResponse): void {
    this.togglingUserId.set(user.id);

    this.company.enableUser(user.id).subscribe({
      next: () => {
        this.togglingUserId.set(null);
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.togglingUserId.set(null);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to update user.'));
      },
    });
  }
}
